import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowLeft, Save, Play, History, FileJson, FlaskConical, ListChecks, Settings2, Bot,
    Download, Copy, Tag, Plus, Trash2, RefreshCcw, ThumbsUp, ThumbsDown, Sparkles,
    Wrench, Layers, X, RotateCcw, ChevronRight, Clock, DollarSign, BarChart2,
} from "lucide-react";
import AgentPlayground from "@/components/AgentPlayground";
import AgentEvals from "@/components/AgentEvals";
import AgentMongoTools from "@/components/mongo/AgentMongoTools";

const TABS = [
    { key: "config", label: "Config", icon: Settings2 },
    { key: "prompt", label: "Prompt", icon: Sparkles },
    { key: "schema", label: "Schemas", icon: FileJson },
    { key: "versions", label: "Versions", icon: History },
    { key: "playground", label: "Playground", icon: FlaskConical },
    { key: "runs", label: "Runs", icon: ListChecks },
    { key: "tests", label: "Test cases", icon: Layers },
    { key: "evals", label: "Evals", icon: BarChart2 },
];

export default function AgentEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState("config");
    const [agent, setAgent] = useState(null);
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState(null);
    const [versionNote, setVersionNote] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [a, t] = await Promise.all([
                api.get(`/admin/agents/${id}`),
                api.get(`/admin/agents/tools`),
            ]);
            setAgent(a.data);
            setDraft(JSON.parse(JSON.stringify(a.data)));
            setTools(t.data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            navigate("/agents");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const dirty = useMemo(() => {
        if (!agent || !draft) return false;
        return JSON.stringify({
            name: agent.name, description: agent.description, purpose: agent.purpose,
            system_prompt: agent.system_prompt, user_prompt_template: agent.user_prompt_template,
            input_schema: agent.input_schema, output_schema: agent.output_schema,
            config: agent.config, tool_access: agent.tool_access, tags: agent.tags, status: agent.status,
        }) !== JSON.stringify({
            name: draft.name, description: draft.description, purpose: draft.purpose,
            system_prompt: draft.system_prompt, user_prompt_template: draft.user_prompt_template,
            input_schema: draft.input_schema, output_schema: draft.output_schema,
            config: draft.config, tool_access: draft.tool_access, tags: draft.tags, status: draft.status,
        });
    }, [agent, draft]);

    const save = async () => {
        if (!dirty) return toast("No changes to save");
        setSaving(true);
        try {
            const body = {
                name: draft.name,
                description: draft.description ?? null,
                purpose: draft.purpose ?? null,
                system_prompt: draft.system_prompt,
                user_prompt_template: draft.user_prompt_template,
                input_schema: draft.input_schema,
                output_schema: draft.output_schema,
                config: draft.config,
                tool_access: draft.tool_access || [],
                tags: draft.tags || [],
                status: draft.status,
                version_note: versionNote || null,
            };
            const { data } = await api.patch(`/admin/agents/${id}`, body);
            setAgent(data);
            setDraft(JSON.parse(JSON.stringify(data)));
            setVersionNote("");
            toast.success(`Saved · v${data.active_version_no}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const exportAgent = async () => {
        try {
            const { data } = await api.get(`/admin/agents/${id}/export`);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `agent-${data.agent.slug}-export.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    if (loading) return <div className="p-8 text-white/40 text-sm">Loading…</div>;
    if (!agent || !draft) return null;

    return (
        <div className="p-6 space-y-5" data-testid="agent-editor">
            <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                    <Link to="/agents" className="mt-1 text-white/40 hover:text-white"><ArrowLeft className="w-4 h-4" /></Link>
                    <div>
                        <div className="flex items-center gap-2">
                            <Bot className="w-4 h-4 text-[#7C3AED]" />
                            <h1 className="font-display text-xl tracking-tight" data-testid="agent-title">{agent.name}</h1>
                            <span className="font-mono text-[10px] text-white/40">v{agent.active_version_no} · {agent.slug}</span>
                            <StatusPill status={agent.status} />
                        </div>
                        <div className="text-xs text-white/50 mt-1">{agent.description || <span className="italic">No description</span>}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={exportAgent} data-testid="agent-export"
                        className="px-3 py-2 border border-white/15 rounded-md text-xs font-medium hover:bg-white/5 flex items-center gap-1.5">
                        <Download className="w-3.5 h-3.5" /> Export
                    </button>
                    <Link to={`/scheduled-tasks?new=agent&target=${id}`} data-testid="agent-schedule"
                        className="px-3 py-2 border border-white/15 rounded-md text-xs font-medium hover:bg-white/5 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> Schedule
                    </Link>
                    {dirty && (
                        <input
                            value={versionNote}
                            onChange={(e) => setVersionNote(e.target.value)}
                            placeholder="Version note (optional)"
                            data-testid="agent-version-note"
                            className="bg-[#0a0a0a] border border-white/15 rounded-md px-3 py-2 text-xs w-56"
                        />
                    )}
                    <button
                        onClick={save}
                        disabled={!dirty || saving}
                        data-testid="agent-save"
                        className={`px-3 py-2 rounded-md text-xs font-medium flex items-center gap-1.5 ${
                            dirty
                                ? "bg-[#7C3AED] text-white hover:bg-[#6D28D9]"
                                : "bg-white/5 text-white/40 cursor-not-allowed"
                        }`}
                    >
                        <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : dirty ? "Save (new version)" : "Saved"}
                    </button>
                </div>
            </div>

            <div className="border-b border-white/10 flex gap-1">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`agent-tab-${t.key}`}
                            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-md ${
                                tab === t.key
                                    ? "bg-[#0a0a0a] text-white border-x border-t border-white/10"
                                    : "text-white/40 hover:text-white/80"
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    );
                })}
            </div>

            <div>
                {tab === "config" && (
                    <>
                        <AgentMongoTools agent={agent} onSaved={(mt) => { setAgent({ ...agent, mongo_tools: mt }); setDraft({ ...draft, mongo_tools: mt }); }} />
                        <ConfigTab draft={draft} setDraft={setDraft} tools={tools} />
                    </>
                )}
                {tab === "prompt" && <PromptTab draft={draft} setDraft={setDraft} />}
                {tab === "schema" && <SchemaTab draft={draft} setDraft={setDraft} />}
                {tab === "versions" && <VersionsTab agentId={id} agent={agent} reload={load} />}
                {tab === "playground" && (
                    <AgentPlayground
                        agent={agent}
                        tools={tools}
                        onRunComplete={() => { /* refresh runs lazily on tab switch */ }}
                    />
                )}
                {tab === "runs" && <RunsTab agentId={id} />}
                {tab === "tests" && <TestCasesTab agentId={id} agent={agent} tools={tools} />}
                {tab === "evals" && <AgentEvals agentId={id} agent={agent} />}
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        draft: "bg-amber-400/10 border-amber-400/30 text-amber-400",
        active: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
        archived: "bg-white/5 border-white/20 text-white/40",
    };
    return (
        <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${map[status] || ""}`}>
            {status}
        </span>
    );
}

// ---------- Config Tab ----------
function ConfigTab({ draft, setDraft, tools }) {
    const cfg = draft.config || {};
    const setCfg = (patch) => setDraft({ ...draft, config: { ...cfg, ...patch } });
    const toolsByCategory = useMemo(() => {
        const groups = {};
        for (const t of tools) {
            (groups[t.category] = groups[t.category] || []).push(t);
        }
        return groups;
    }, [tools]);

    const toggleTool = (name) => {
        const current = draft.tool_access || [];
        const next = current.includes(name) ? current.filter((n) => n !== name) : [...current, name];
        setDraft({ ...draft, tool_access: next });
    };

    return (
        <div className="grid grid-cols-2 gap-6 mt-5">
            <div className="space-y-4">
                <Section title="Identity">
                    <Field label="Name">
                        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                            data-testid="cfg-name" className="input" />
                    </Field>
                    <Field label="Status">
                        <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                            data-testid="cfg-status" className="input">
                            <option value="draft">Draft</option>
                            <option value="active">Active</option>
                            <option value="archived">Archived</option>
                        </select>
                    </Field>
                    <Field label="Description">
                        <textarea value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                            rows={3} className="input" />
                    </Field>
                    <Field label="Purpose / use case">
                        <textarea value={draft.purpose || ""} onChange={(e) => setDraft({ ...draft, purpose: e.target.value })}
                            rows={2} className="input" placeholder="Why does this agent exist? Who uses it?" />
                    </Field>
                    <Field label="Tags (comma-separated)">
                        <input value={(draft.tags || []).join(", ")}
                            onChange={(e) => setDraft({ ...draft, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                            className="input" />
                    </Field>
                </Section>
            </div>

            <div className="space-y-4">
                <Section title="Model & sampling">
                    <Field label="Provider">
                        <select value={cfg.provider} onChange={(e) => setCfg({ provider: e.target.value })}
                            data-testid="cfg-provider" className="input">
                            <option value="anthropic">Anthropic Claude Sonnet 5.5</option>
                            <option value="gemini">Google Gemini 2.5 Flash</option>
                        </select>
                    </Field>
                    <Field label="Model">
                        <input value={cfg.model || ""} onChange={(e) => setCfg({ model: e.target.value })}
                            data-testid="cfg-model" className="input font-mono text-xs" />
                    </Field>
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Temperature">
                            <input type="number" min="0" max="2" step="0.05" value={cfg.temperature ?? 0.2}
                                onChange={(e) => setCfg({ temperature: Number(e.target.value) })}
                                data-testid="cfg-temperature" className="input font-mono" />
                        </Field>
                        <Field label="Max tokens">
                            <input type="number" min="1" value={cfg.max_tokens ?? 4096}
                                onChange={(e) => setCfg({ max_tokens: Number(e.target.value) })}
                                data-testid="cfg-max-tokens" className="input font-mono" />
                        </Field>
                        <Field label="Top P">
                            <input type="number" min="0" max="1" step="0.05" value={cfg.top_p ?? ""}
                                onChange={(e) => setCfg({ top_p: e.target.value === "" ? null : Number(e.target.value) })}
                                className="input font-mono" placeholder="auto" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Context window">
                            <input type="number" value={cfg.context_window ?? 200000}
                                onChange={(e) => setCfg({ context_window: Number(e.target.value) })}
                                className="input font-mono" />
                        </Field>
                        <Field label="Response format">
                            <select value={cfg.response_format || "json"} onChange={(e) => setCfg({ response_format: e.target.value })}
                                className="input">
                                <option value="json">JSON object</option>
                                <option value="text">Free text</option>
                            </select>
                        </Field>
                    </div>
                </Section>

                <Section title="Tool access" icon={<Wrench className="w-3.5 h-3.5" />}>
                    {Object.entries(toolsByCategory).map(([cat, ts]) => (
                        <div key={cat} className="space-y-1.5">
                            <div className="label-xs text-white/40 uppercase">{cat}</div>
                            {ts.map((t) => {
                                const enabled = (draft.tool_access || []).includes(t.name);
                                return (
                                    <label key={t.name} className="flex items-start gap-2 cursor-pointer text-xs hover:bg-white/[0.02] rounded p-1.5">
                                        <input type="checkbox" checked={enabled} onChange={() => toggleTool(t.name)}
                                            data-testid={`cfg-tool-${t.name}`} className="mt-0.5" />
                                        <div className="flex-1">
                                            <div className="font-mono text-[11px] text-white/80">{t.name}</div>
                                            <div className="text-[10px] text-white/40">{t.description}</div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    ))}
                </Section>
            </div>
        </div>
    );
}

// ---------- Prompt Tab ----------
function PromptTab({ draft, setDraft }) {
    return (
        <div className="grid grid-cols-1 gap-5 mt-5">
            <Section title="System prompt">
                <textarea
                    value={draft.system_prompt || ""}
                    onChange={(e) => setDraft({ ...draft, system_prompt: e.target.value })}
                    data-testid="prompt-system"
                    rows={10}
                    className="input font-mono text-xs"
                    placeholder="You are a clinical research assistant..."
                />
                <div className="text-[10px] text-white/40 mt-2">
                    The system prompt is sent on every call. Keep instructions stable and concise here.
                </div>
            </Section>

            <Section title="User prompt template">
                <textarea
                    value={draft.user_prompt_template || ""}
                    onChange={(e) => setDraft({ ...draft, user_prompt_template: e.target.value })}
                    data-testid="prompt-user-template"
                    rows={12}
                    className="input font-mono text-xs"
                    placeholder={`## Input\n{{input.protocol_number}}\n\n## Context\n{{context}}`}
                />
                <div className="text-[10px] text-white/40 mt-2">
                    Use <code className="px-1 bg-white/5 rounded text-[#7C3AED]">{`{{variable}}`}</code> placeholders. Inputs from the playground are flattened into this scope. The reserved <code className="px-1 bg-white/5 rounded text-[#7C3AED]">{`{{context}}`}</code> placeholder receives tool results auto-loaded by the playground.
                </div>
            </Section>
        </div>
    );
}

// ---------- Schema Tab ----------
function SchemaTab({ draft, setDraft }) {
    const [inputErr, setInputErr] = useState(null);
    const [outputErr, setOutputErr] = useState(null);
    const [inputText, setInputText] = useState(JSON.stringify(draft.input_schema || {}, null, 2));
    const [outputText, setOutputText] = useState(JSON.stringify(draft.output_schema || {}, null, 2));

    const apply = () => {
        try {
            const i = JSON.parse(inputText || "{}");
            setInputErr(null);
            setDraft({ ...draft, input_schema: i });
        } catch (e) {
            setInputErr(e.message);
        }
        try {
            const o = JSON.parse(outputText || "{}");
            setOutputErr(null);
            setDraft({ ...draft, output_schema: o });
        } catch (e) {
            setOutputErr(e.message);
        }
    };

    return (
        <div className="grid grid-cols-2 gap-5 mt-5">
            <Section title="Input schema (JSON Schema-like)">
                <textarea value={inputText} onChange={(e) => setInputText(e.target.value)}
                    data-testid="schema-input" rows={20} className="input font-mono text-[11px]" />
                {inputErr && <div className="text-[10px] text-red-400 mt-1">{inputErr}</div>}
            </Section>
            <Section title="Output schema">
                <textarea value={outputText} onChange={(e) => setOutputText(e.target.value)}
                    data-testid="schema-output" rows={20} className="input font-mono text-[11px]" />
                {outputErr && <div className="text-[10px] text-red-400 mt-1">{outputErr}</div>}
            </Section>
            <div className="col-span-2">
                <button onClick={apply} data-testid="schema-apply"
                    className="px-3 py-2 bg-[#7C3AED] text-white rounded-md text-xs font-medium hover:bg-[#6D28D9]">
                    Apply schemas to draft
                </button>
                <div className="text-[10px] text-white/40 mt-2">
                    Click "Save (new version)" at top right to persist. Schemas are stored as plain JSON; tools may use them later for form generation in the Playground.
                </div>
            </div>
        </div>
    );
}

// ---------- Versions Tab ----------
function VersionsTab({ agentId, agent, reload }) {
    const [versions, setVersions] = useState([]);
    const [compare, setCompare] = useState({ left: null, right: null });
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/agents/${agentId}/versions`);
            setVersions(data.items || []);
            if (data.items?.length) setCompare({ right: data.items[0], left: data.items[1] || null });
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

    const rollback = async (vno) => {
        if (!window.confirm(`Roll back to version ${vno}? A new version copy will be created (audit-friendly).`)) return;
        try {
            await api.post(`/admin/agents/${agentId}/versions/${vno}/rollback`);
            toast.success(`Rolled back to v${vno}`);
            await reload();
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    if (loading) return <div className="p-6 text-white/40 text-sm">Loading…</div>;

    return (
        <div className="grid grid-cols-3 gap-5 mt-5">
            <div className="border border-white/10 bg-[#0a0a0a] rounded-md max-h-[600px] overflow-y-auto">
                {versions.map((v) => (
                    <button key={v.id} onClick={() => setCompare({ ...compare, right: v })}
                        data-testid={`version-row-${v.version_no}`}
                        className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.02] ${
                            compare.right?.id === v.id ? "bg-white/5" : ""
                        }`}>
                        <div className="flex items-center justify-between">
                            <div className="font-mono text-xs">v{v.version_no}</div>
                            {agent.active_version_no === v.version_no && (
                                <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-full border border-emerald-400/30 text-emerald-400">active</span>
                            )}
                        </div>
                        <div className="text-[10px] text-white/40 mt-0.5">{v.note || "—"}</div>
                        <div className="font-mono text-[10px] text-white/30 mt-1">{v.created_at && new Date(v.created_at).toLocaleString()}</div>
                    </button>
                ))}
            </div>
            <div className="col-span-2 space-y-3">
                {compare.right ? (
                    <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs text-white/40">Selected version</div>
                                <div className="font-mono">v{compare.right.version_no} — {compare.right.note}</div>
                            </div>
                            {agent.active_version_no !== compare.right.version_no && (
                                <button onClick={() => rollback(compare.right.version_no)} data-testid={`rollback-${compare.right.version_no}`}
                                    className="px-3 py-1.5 bg-amber-400/10 border border-amber-400/30 text-amber-400 rounded-md text-xs font-medium flex items-center gap-1.5 hover:bg-amber-400/20">
                                    <RotateCcw className="w-3.5 h-3.5" /> Roll back to this version
                                </button>
                            )}
                        </div>
                        <div>
                            <div className="label-xs mb-1">System prompt</div>
                            <pre className="bg-[#050505] border border-white/10 rounded-md p-3 text-[11px] font-mono whitespace-pre-wrap max-h-60 overflow-auto">{compare.right.system_prompt || "—"}</pre>
                        </div>
                        <div>
                            <div className="label-xs mb-1">User prompt template</div>
                            <pre className="bg-[#050505] border border-white/10 rounded-md p-3 text-[11px] font-mono whitespace-pre-wrap max-h-60 overflow-auto">{compare.right.user_prompt_template || "—"}</pre>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="label-xs mb-1">Config</div>
                                <pre className="bg-[#050505] border border-white/10 rounded-md p-2 text-[10px] font-mono">{JSON.stringify(compare.right.config, null, 2)}</pre>
                            </div>
                            <div>
                                <div className="label-xs mb-1">Tool access</div>
                                <pre className="bg-[#050505] border border-white/10 rounded-md p-2 text-[10px] font-mono">{JSON.stringify(compare.right.tool_access, null, 2)}</pre>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-white/40 text-sm">Select a version to inspect.</div>
                )}
            </div>
        </div>
    );
}

// ---------- Runs Tab ----------
function RunsTab({ agentId }) {
    const [runs, setRuns] = useState([]);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/agents/${agentId}/runs?limit=100${filter ? `&rating=${filter}` : ""}`);
            setRuns(data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [agentId, filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const showDetail = async (id) => {
        const { data } = await api.get(`/admin/agents/${agentId}/runs/${id}`);
        setDetail(data);
    };

    const rate = async (run, rating) => {
        try {
            const { data } = await api.post(`/admin/agents/${agentId}/runs/${run.id}/rate`, { rating });
            toast.success(`Marked ${rating}`);
            setDetail(data);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="mt-5 space-y-4">
            <div className="flex items-center gap-2">
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input text-xs max-w-[200px]"
                    data-testid="runs-rating-filter">
                    <option value="">All runs</option>
                    <option value="good">Good</option>
                    <option value="bad">Bad</option>
                    <option value="unrated">Unrated</option>
                </select>
                <button onClick={load} className="px-3 py-2 border border-white/15 rounded-md text-xs hover:bg-white/5 flex items-center gap-1.5">
                    <RefreshCcw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>
            <div className="border border-white/10 bg-[#0a0a0a] rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-white/[0.02] border-b border-white/10">
                        <tr className="text-left label-xs text-white/40">
                            <th className="px-4 py-3">When</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">v</th>
                            <th className="px-4 py-3">Source</th>
                            <th className="px-4 py-3">Latency</th>
                            <th className="px-4 py-3">Tokens</th>
                            <th className="px-4 py-3">Cost</th>
                            <th className="px-4 py-3">Rating</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="px-4 py-6 text-center text-white/40">Loading…</td></tr>
                        ) : runs.length === 0 ? (
                            <tr><td colSpan={8} className="px-4 py-8 text-center text-white/40">No runs yet — head to Playground.</td></tr>
                        ) : runs.map((r) => (
                            <tr key={r.id} onClick={() => showDetail(r.id)}
                                data-testid={`run-row-${r.id}`}
                                className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer">
                                <td className="px-4 py-3 font-mono text-[10px] text-white/60">{new Date(r.created_at).toLocaleString()}</td>
                                <td className="px-4 py-3"><RunStatus status={r.status} /></td>
                                <td className="px-4 py-3 font-mono text-xs">v{r.version_no}</td>
                                <td className="px-4 py-3 font-mono text-[10px] text-white/60">{r.source}</td>
                                <td className="px-4 py-3 font-mono text-xs">{r.latency_ms} ms</td>
                                <td className="px-4 py-3 font-mono text-xs">{r.tokens_in}/{r.tokens_out}</td>
                                <td className="px-4 py-3 font-mono text-xs">${(r.total_cost_usd || 0).toFixed(5)}</td>
                                <td className="px-4 py-3"><RatingPill rating={r.rating} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {detail && (
                <RunDetailModal run={detail} onClose={() => setDetail(null)} onRate={(r) => rate(detail, r)} />
            )}
        </div>
    );
}

function RunStatus({ status }) {
    const map = {
        completed: "text-emerald-400",
        failed: "text-red-400",
        running: "text-amber-400",
        queued: "text-white/40",
    };
    return <span className={`text-[11px] font-mono ${map[status] || ""}`}>{status}</span>;
}

function RatingPill({ rating }) {
    if (rating === "good") return <span className="text-[10px] text-emerald-400 flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> good</span>;
    if (rating === "bad") return <span className="text-[10px] text-red-400 flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> bad</span>;
    return <span className="text-[10px] text-white/30">—</span>;
}

function RunDetailModal({ run, onClose, onRate }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" data-testid="run-detail-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <ListChecks className="w-4 h-4 text-[#7C3AED] mr-2" />
                    <div className="font-display text-lg tracking-tight">Run {run.id.slice(0, 8)}</div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div className="grid grid-cols-4 gap-3 text-xs">
                        <Stat label="Status" value={run.status} />
                        <Stat label="Latency" value={`${run.latency_ms} ms`} icon={<Clock className="w-3 h-3" />} />
                        <Stat label="Tokens" value={`${run.tokens_in} / ${run.tokens_out}`} />
                        <Stat label="Cost" value={`$${(run.total_cost_usd || 0).toFixed(5)}`} icon={<DollarSign className="w-3 h-3" />} />
                    </div>
                    {run.error && <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded-md p-3">{run.error}</div>}
                    <details>
                        <summary className="text-xs text-white/60 cursor-pointer">Rendered prompts</summary>
                        <div className="mt-2">
                            <div className="label-xs">System</div>
                            <pre className="bg-[#050505] border border-white/10 rounded-md p-2 text-[10px] font-mono whitespace-pre-wrap max-h-40 overflow-auto">{run.rendered_system_prompt || "—"}</pre>
                            <div className="label-xs mt-2">User</div>
                            <pre className="bg-[#050505] border border-white/10 rounded-md p-2 text-[10px] font-mono whitespace-pre-wrap max-h-40 overflow-auto">{run.rendered_user_prompt || "—"}</pre>
                        </div>
                    </details>
                    <div>
                        <div className="label-xs">Output</div>
                        <pre className="bg-[#050505] border border-white/10 rounded-md p-2 text-[11px] font-mono max-h-60 overflow-auto" data-testid="run-detail-output">
                            {run.output_payload ? JSON.stringify(run.output_payload, null, 2) : (run.output_text || run.raw_response || "—")}
                        </pre>
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                        <button onClick={() => onRate("good")} data-testid="run-rate-good"
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border flex items-center gap-1.5 ${
                                run.rating === "good" ? "bg-emerald-400/10 border-emerald-400/40 text-emerald-400" : "border-white/15 text-white/60 hover:text-white"
                            }`}>
                            <ThumbsUp className="w-3.5 h-3.5" /> Good
                        </button>
                        <button onClick={() => onRate("bad")} data-testid="run-rate-bad"
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border flex items-center gap-1.5 ${
                                run.rating === "bad" ? "bg-red-400/10 border-red-400/40 text-red-400" : "border-white/15 text-white/60 hover:text-white"
                            }`}>
                            <ThumbsDown className="w-3.5 h-3.5" /> Bad
                        </button>
                        <button onClick={() => onRate("unrated")}
                            className="px-3 py-1.5 rounded-md text-xs font-medium border border-white/15 text-white/40 hover:text-white">
                            Clear
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, icon }) {
    return (
        <div className="border border-white/10 rounded-md p-3 bg-[#050505]">
            <div className="label-xs text-white/40 flex items-center gap-1.5">{icon} {label}</div>
            <div className="font-mono text-sm mt-0.5">{value}</div>
        </div>
    );
}

// ---------- Test Cases Tab ----------
function TestCasesTab({ agentId, agent, tools }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/agents/${agentId}/testcases`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

    const remove = async (id) => {
        if (!window.confirm("Delete this test case?")) return;
        await api.delete(`/admin/agents/${agentId}/testcases/${id}`);
        load();
    };

    const runTC = async (id) => {
        try {
            const { data } = await api.post(`/admin/agents/${agentId}/testcases/${id}/run`);
            toast.success(`Test ${data.test_case_status === "pass" ? "PASSED" : data.test_case_status === "fail" ? "FAILED" : "ran"}`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="mt-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs text-white/50">Saved scenarios that can be replayed against any version for regression checking.</div>
                <button onClick={() => setCreating(true)} data-testid="testcase-create"
                    className="px-3 py-2 bg-[#7C3AED] text-white rounded-md text-xs font-medium hover:bg-[#6D28D9] flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> New test case
                </button>
            </div>
            <div className="border border-white/10 bg-[#0a0a0a] rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-white/[0.02] border-b border-white/10">
                        <tr className="text-left label-xs text-white/40">
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Last result</th>
                            <th className="px-4 py-3">Created</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={4} className="px-4 py-8 text-center text-white/40">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={4} className="px-4 py-8 text-center text-white/40">No test cases yet.</td></tr>
                        ) : items.map((tc) => (
                            <tr key={tc.id} className="border-b border-white/5">
                                <td className="px-4 py-3">
                                    <div className="font-medium">{tc.name}</div>
                                    <div className="text-[10px] text-white/40">{tc.description || ""}</div>
                                </td>
                                <td className="px-4 py-3">
                                    {tc.last_status === "pass" && <span className="text-xs text-emerald-400">PASS</span>}
                                    {tc.last_status === "fail" && <span className="text-xs text-red-400">FAIL</span>}
                                    {tc.last_status === "unknown" && <span className="text-xs text-white/40">unknown</span>}
                                    {!tc.last_status && <span className="text-xs text-white/30">—</span>}
                                </td>
                                <td className="px-4 py-3 font-mono text-[10px] text-white/40">{new Date(tc.created_at).toLocaleString()}</td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-1.5">
                                        <button onClick={() => runTC(tc.id)} title="Run" data-testid={`testcase-run-${tc.id}`}
                                            className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white">
                                            <Play className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => remove(tc.id)} title="Delete"
                                            className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {creating && (
                <CreateTestCaseModal agentId={agentId} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
            )}
        </div>
    );
}

function CreateTestCaseModal({ agentId, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [inputJson, setInputJson] = useState("{}");
    const [expectedJson, setExpectedJson] = useState("{}");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return toast.error("Name required");
        try {
            const input_payload = JSON.parse(inputJson || "{}");
            const expected_output = expectedJson.trim() ? JSON.parse(expectedJson) : null;
            setBusy(true);
            await api.post(`/admin/agents/${agentId}/testcases`, {
                name, description: desc || null, input_payload, expected_output, context_attachments: [],
            });
            toast.success("Test case saved");
            onCreated();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Invalid JSON");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-[#0a0a0a] border border-white/15 rounded-md">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <Layers className="w-4 h-4 text-[#7C3AED] mr-2" />
                    <div className="font-display text-lg">New test case</div>
                    <button onClick={onClose} className="ml-auto text-white/60"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name *"
                        data-testid="testcase-name" className="input" />
                    <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description"
                        className="input" />
                    <div>
                        <div className="label-xs mb-1">Input payload (JSON)</div>
                        <textarea value={inputJson} onChange={(e) => setInputJson(e.target.value)}
                            data-testid="testcase-input" rows={6} className="input font-mono text-[11px]" />
                    </div>
                    <div>
                        <div className="label-xs mb-1">Expected output (JSON, optional — used for pass/fail)</div>
                        <textarea value={expectedJson} onChange={(e) => setExpectedJson(e.target.value)}
                            rows={6} className="input font-mono text-[11px]" placeholder='{"summary": "..."}' />
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                        <button type="submit" disabled={busy} data-testid="testcase-submit"
                            className="flex-1 bg-[#7C3AED] text-white py-2 rounded-md text-xs font-medium hover:bg-[#6D28D9] disabled:opacity-50">
                            {busy ? "Saving…" : "Save test case"}
                        </button>
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-white/15 rounded-md text-xs hover:bg-white/5">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ---------- shared bits ----------
function Section({ title, icon, children }) {
    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-5 space-y-3">
            <div className="label-xs text-white/40 flex items-center gap-1.5">{icon} {title}</div>
            {children}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="label-xs text-white/40 mb-1.5">{label}</div>
            {children}
        </label>
    );
}
